import { Box, Divider, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { DateLabel } from "klayowebshared";
import { isEmpty } from "lodash";
import { Component } from "react";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import { InlineDocumentList } from "./InlineDocumentList";
import { ProfilePicture } from "./ProfilePicture";

interface RequestActivityItemProps extends WithLocaleProps {
  activity: any;
  user: any;
  employee?: any;
  documents?: any;
  onDocumentClick?: (doc: any) => void;
  checklists?: any;
  onChecklistClick?: (checklist: any) => void;
  theme?: any;
}

// Regex compiled once for performance
const COMMENT_REASON_KEY_REGEX = /commentReasons\.\w+/g;
const COMMENT_REASON_PREFIX = "commentReasons.";
// Regex to strip trailing punctuation from keys (backend may append periods)
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?]+$/;

class RequestActivityItemBase extends Component<RequestActivityItemProps, any> {
  private translateCommentReason(commentReason: string): string {
    const { translate } = this.props;

    // Fast path: null/undefined/empty check
    if (!commentReason) return commentReason;

    // Safety check: if translate is not available, return as-is
    if (!translate) return commentReason;

    // Fast path: doesn't contain translation keys - return as-is (historical data)
    if (!commentReason.includes(COMMENT_REASON_PREFIX)) {
      return commentReason;
    }

    // Get the attribute name for interpolation (org-specific terminology)
    const attributeName = translate("entities.attribute");
    const interpolationParams = { attribute: attributeName };

    // Fast path: simple key without spaces (most common case for new data)
    // Strip trailing punctuation before looking up the key, then re-add it after translation
    if (!commentReason.includes(" ") && commentReason.startsWith(COMMENT_REASON_PREFIX)) {
      const trailingPunctuation = commentReason.match(TRAILING_PUNCTUATION_REGEX)?.[0] || "";
      const cleanKey = commentReason.replace(TRAILING_PUNCTUATION_REGEX, "");
      const translated = translate(cleanKey, interpolationParams);
      // Re-add trailing punctuation to preserve sentence formatting
      return translated + trailingPunctuation;
    }

    // Handle concatenated keys with values (e.g., "commentReasons.proficiencySetTo 5")
    // Replace each key with its translation, preserving values between keys
    return commentReason.replace(COMMENT_REASON_KEY_REGEX, (key) =>
      translate(key, interpolationParams)
    );
  }

  render() {
    const { activity, user, documents, onDocumentClick, checklists, onChecklistClick, theme } =
      this.props;

    return (
      <Box className='klayo-feeditem'>
        <ListItem>
          <ListItemIcon sx={{ alignSelf: "start" }}>
            <ProfilePicture
              size='md'
              user={activity.user}
              src={activity.user?.getProfilePictureUrl()}
            />
          </ListItemIcon>
          <ListItemText className='klayo-feeditem_content' disableTypography={true}>
            <DateLabel
              className='klayo-feeditem_date'
              date={activity.createDate}
              sx={{ marginBottom: "10px" }}
            />
            <div className='klayo-feeditem_user'>
              {user?.isMe(activity.user)
                ? this.props.translate("activity.you")
                : activity.employeeFullName}
            </div>
            <div className='klayo-feeditem_status'>
              {this.translateCommentReason(activity.commentReason)}
            </div>
            {(!isEmpty(activity?.documentIds) || !isEmpty(activity?.checklistAssignmentIds)) && (
              <div className='klayo-feeditem_documents'>
                <InlineDocumentList
                  onDocumentClick={onDocumentClick}
                  onChecklistClick={onChecklistClick}
                  documents={documents}
                  filter={activity.documentIds}
                  checklistFilter={activity.checklistAssignmentIds}
                  sx={{ display: "flex", paddingBottom: "10px" }}
                  checklists={checklists}
                  theme={theme}
                />
              </div>
            )}
            {activity.text && activity.text.length && (
              <div className='klayo-feeditem_comment'>
                <span>
                  {this.props.translate("activity.commentAdded")} {activity.text}
                </span>
              </div>
            )}
          </ListItemText>
        </ListItem>
        <Divider />
      </Box>
    );
  }
}

export const RequestActivityItem = withLocale(RequestActivityItemBase);
